import { EnvPill } from "@/components/iris";
import { ActivityCharts } from "@/features/statistics/activity-charts";
import { EntityBreakdowns } from "@/features/statistics/entity-breakdowns";
import { GroupBreakdowns } from "@/features/statistics/group-breakdowns";
import { KpiCards } from "@/features/statistics/kpi-cards";
import { useRefreshStatistics, useStatistics } from "@/hooks/use-statistics";
import { cn } from "@/lib/cn";
import type { Env } from "@/lib/types";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_authed/analytics")({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  // IR-1046 (Rotem 2026-06-09): scope the tab to the selected env — dev/prod
  // run independent DBs, so their stats diverge. Default prod (prior behavior).
  const [env, setEnv] = useState<Env>("prod");
  const { data, isLoading, error } = useStatistics(env);
  const refreshMutation = useRefreshStatistics(env);

  // Refresh recomputes the LOCAL (prod) DB only; dev refreshes via its own
  // daily job, so disable the button on the dev toggle (BE also rejects it).
  const refreshDisabled = refreshMutation.isPending || env === "dev";

  const timeAgo = data?.computed_at ? formatTimeAgo(new Date(data.computed_at)) : null;

  return (
    <div className="iris-page">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 className="iris-page__title" style={{ fontSize: 32, marginBottom: 4 }}>
            Analytics
          </h1>
          {timeAgo && (
            <span className="iris-meta">
              Last computed {timeAgo}
              {data?.computation_time_ms != null && (
                <span style={{ marginLeft: 8, color: "var(--iris-fg-5)" }}>
                  ({data.computation_time_ms}ms)
                </span>
              )}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <EnvPill value={env} onChange={setEnv} />
          <button
            type="button"
            className={cn("iris-btn", refreshDisabled && "iris-btn--disabled")}
            onClick={() => refreshMutation.mutate()}
            disabled={refreshDisabled}
            title={
              env === "dev"
                ? "Refresh recomputes production only — the dev snapshot updates via the dev daily job."
                : undefined
            }
          >
            <RefreshCw size={14} className={cn(refreshMutation.isPending && "animate-spin")} />
            {refreshMutation.isPending ? "Computing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 16 }}>
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders
              key={i}
              className="iris-card"
              style={{ height: 128, opacity: 0.5 }}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (error || !data) && (
        <div
          className="iris-card"
          style={{
            padding: 48,
            textAlign: "center",
            color: "var(--iris-fg-4)",
            fontSize: "var(--iris-t-body)",
          }}
        >
          No statistics computed yet. Click Refresh to generate.
        </div>
      )}

      {/* Data */}
      {data && (
        <>
          <KpiCards kpis={data.kpis} isRefreshing={refreshMutation.isPending} />

          <EntityBreakdowns
            breakdowns={data.entity_breakdowns}
            totalEntities={data.kpis.total_entities}
          />

          <GroupBreakdowns breakdowns={data.group_breakdowns} />

          <ActivityCharts
            entities={data.activity.new_entities_daily}
            messages={data.activity.new_messages_daily}
            groups={data.activity.new_groups_daily}
          />
        </>
      )}
    </div>
  );
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
